import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, RefreshCw, Copy, AlertTriangle } from "lucide-react";

const STATO = {
  new: ["Nuovo", "badge-verde"], update: ["Aggiornamento", "bg-sky-50 text-sky-800 border-sky-200"],
  duplicate: ["Duplicato nel file", "badge-giallo"], error: ["Errore", "badge-rosso"],
};

export default function ImportAnagraficaDialog({ open, onOpenChange, entity, label, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("update");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (open) { setFile(null); setPreview(null); setResult(null); setMode("update"); }
  }, [open]);

  const onFile = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/${entity}/import/preview`, fd);
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e));
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await api.post(`/${entity}/import/commit`, fd);
      setResult(data);
      onImported?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const c = preview?.counts;
  const importable = c ? c.new + (mode === "update" ? c.update : 0) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="import-anagrafica-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Importa {label} da Excel</DialogTitle>
          <DialogDescription>Usa il modello scaricabile. I duplicati sono riconosciuti tramite {preview?.key_labels?.join(" / ") || "chiave univoca"}: i record esistenti vengono aggiornati solo con i campi compilati nel file.</DialogDescription>
        </DialogHeader>

        {!preview && !result && (
          <div className="py-6">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center cursor-pointer hover:border-sky-400 transition-colors" onClick={() => fileRef.current?.click()} data-testid="import-anag-dropzone">
              {busy ? <Loader2 className="h-8 w-8 animate-spin mx-auto text-sky-500" /> : <Upload className="h-8 w-8 mx-auto text-slate-400" />}
              <div className="mt-3 text-sm text-slate-600">Clicca per selezionare il file <strong>.xlsx</strong> o <strong>.csv</strong></div>
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" data-testid="import-anag-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
          </div>
        )}

        {preview && !result && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              {[["new", CheckCircle2, "Nuovi"], ["update", RefreshCw, "Da aggiornare"], ["duplicate", Copy, "Duplicati nel file"], ["error", AlertTriangle, "Errori"]].map(([k, Icon, l]) => (
                <div key={k} className={`${STATO[k][1]} rounded-md p-3 border`}>
                  <div className="text-xs font-semibold uppercase flex items-center gap-1"><Icon className="h-4 w-4" />{l}</div>
                  <div className="text-2xl font-head font-bold" data-testid={`import-anag-count-${k}`}>{c[k]}</div>
                </div>
              ))}
            </div>
            {preview.unmapped.length > 0 && <div className="text-xs text-amber-700">Colonne ignorate (non riconosciute): {preview.unmapped.join(", ")}</div>}
            {c.update > 0 && (
              <div className="flex gap-4 text-sm items-center" data-testid="import-anag-mode">
                <span className="text-slate-600">Record già esistenti:</span>
                <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" checked={mode === "update"} onChange={() => setMode("update")} data-testid="import-anag-mode-update" />Aggiorna con i campi compilati</label>
                <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" checked={mode === "skip"} onChange={() => setMode("skip")} data-testid="import-anag-mode-skip" />Salta, non modificare</label>
              </div>
            )}
            <div className="border border-slate-200 rounded-md max-h-72 overflow-auto text-xs">
              <table className="w-full" data-testid="import-anag-rows">
                <thead className="bg-slate-50 sticky top-0"><tr><th className="text-left px-2 py-1">Riga</th><th className="text-left px-2 py-1">Record</th><th className="text-left px-2 py-1">Esito</th><th className="text-left px-2 py-1">Dettaglio</th></tr></thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row} className="border-t border-slate-100">
                      <td className="px-2 py-1">{r.row}</td>
                      <td className="px-2 py-1 font-medium">{r.label}</td>
                      <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded border ${STATO[r.status][1]}`}>{STATO[r.status][0]}</span></td>
                      <td className="px-2 py-1 text-slate-600">{[r.match_key ? `corrisponde per ${r.match_key}` : null, ...r.errors, ...r.warnings].filter(Boolean).join("; ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }}>Altro file</Button>
              <Button onClick={commit} disabled={busy || importable === 0} data-testid="import-anag-commit">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Importa {importable} record
              </Button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-4 py-2" data-testid="import-anag-result">
            <div className="grid grid-cols-4 gap-3">
              <div className="badge-verde rounded-md p-3 border"><div className="text-xs font-semibold uppercase">Inseriti</div><div className="text-2xl font-head font-bold" data-testid="import-anag-inserted">{result.inserted}</div></div>
              <div className="bg-sky-50 text-sky-800 border-sky-200 rounded-md p-3 border"><div className="text-xs font-semibold uppercase">Aggiornati</div><div className="text-2xl font-head font-bold">{result.updated}</div></div>
              <div className="badge-giallo rounded-md p-3 border"><div className="text-xs font-semibold uppercase">Saltati</div><div className="text-2xl font-head font-bold">{result.skipped}</div></div>
              <div className="badge-rosso rounded-md p-3 border"><div className="text-xs font-semibold uppercase">Errori</div><div className="text-2xl font-head font-bold">{result.errors}</div></div>
            </div>
            <DialogFooter><Button onClick={() => onOpenChange(false)} data-testid="import-anag-close">Chiudi</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
